"use client";

import { FC, SyntheticEvent, useContext, useEffect, useRef, useState, KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import {
    IChatResponse,
    IDeleteChatBulkRequest,
    IUpdateChatBulkRequest,
} from "@finbackoffice/clientbff-client";
import classNames from "classnames";
import { usePathname, useRouter } from "next/navigation";
import {
    ClientBFFContext,
    formatSearchParamsToString,
    useSearchParamsQuery,
} from "@finbackoffice/site-core";
import { AssetsDirectory, TranslationScopes } from "@finbackoffice/enums";
import Button from "components/base/button/Button";
import Translate from "components/base/translate/Translate";
import { Svg } from "components/base/svg/Svg";
import Input, { OnChangeHandler } from "components/base/input-field/Input";
import styles from "./messaging-tools.module.sass";

const MessagingSearch: FC = () => {
    const { t } = useTranslation();
    const router = useRouter();
    const pathname = usePathname();
    const { query } = useSearchParamsQuery();
    const { search } = query;
    const [inputVisible, setInputVisible] = useState(!!search);
    const searchInputValueRef = useRef("");
    const [searchQuery, setSearchQuery] = useState<string>((search as string) || "");

    const onIconClickHandler = () => {
        if (!inputVisible) {
            setInputVisible(true);
        }
    };

    const onInnerIconClickHandler = () => {
        if (inputVisible) {
            setInputVisible(false);
            searchInputValueRef.current = "";
            setSearchQuery("");

            if (search) {
                const q = { ...query };
                delete q.search;
                router.push(`${pathname}?${formatSearchParamsToString(q)}`);
            }
        }
    };

    const handleChange: OnChangeHandler<false> = (val: string) => {
        searchInputValueRef.current = val;
        setSearchQuery(searchInputValueRef.current);
    };

    const onKeydownHandler = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            if (searchInputValueRef.current) {
                router.push(
                    `${pathname}?${formatSearchParamsToString({ ...query, search: searchInputValueRef.current })}`,
                );
            }
        }
    };

    return (
        <Input
            type="search"
            name="query"
            placeholder={t("account_messages_searchPlaceholder", {
                ns: TranslationScopes.UserAccount,
            })}
            iconClass={styles.searchBtn}
            wrapperClassname={classNames(
                styles.searchField,
                inputVisible && styles.searchInputVisible,
            )}
            onIconClick={onIconClickHandler}
            onInnerIconClick={onInnerIconClickHandler}
            innerIconClass={styles.closeBtn}
            value={searchQuery}
            showFocus={inputVisible}
            onChange={handleChange}
            onKeyDown={onKeydownHandler}
        />
    );
};

interface IProps {
    triggerSelectAll: (val: boolean) => void;
    selectedIds: number[];
    starValue: boolean;
    count: number;
    onStarrClickCallback?: (items: IChatResponse[]) => void;
    onRemoveClickCallback?: (items: IDeleteChatBulkRequest[]) => void;
}

const MessagingTools: FC<IProps> = ({
    triggerSelectAll,
    selectedIds,
    starValue,
    count,
    onStarrClickCallback,
    onRemoveClickCallback,
}) => {
    const client = useContext(ClientBFFContext);
    const [allSelected, setAllSelected] = useState(false);

    useEffect(() => {
        setAllSelected(count === selectedIds.length && count !== 0);
    }, [count, selectedIds]);

    const onSelectAllClick = (e: SyntheticEvent) => {
        e.preventDefault();
        triggerSelectAll(!allSelected);
        setAllSelected(!allSelected);
    };

    const onRemoveClick = async () => {
        if (selectedIds.length) {
            const body: IDeleteChatBulkRequest[] = [];
            selectedIds.forEach((id) => body.push({ chat_id: id }));

            try {
                await client.deleteChatBulk(body);

                onRemoveClickCallback?.(body);
            } catch (error) {
                console.error(error);
            }

            if (allSelected) {
                setAllSelected(false);
            }

            triggerSelectAll(false);
        }
    };

    const onStarrClick = async () => {
        if (selectedIds.length) {
            const body: IUpdateChatBulkRequest[] = [];
            selectedIds.forEach((id) =>
                body.push({
                    chat_id: id,
                    is_starred: !starValue,
                }),
            );

            try {
                const response = await client.updateChatBulk(body);

                if (response) {
                    onStarrClickCallback?.(response.items);
                }
            } catch (error) {
                console.error(error);
            }

            if (allSelected) {
                setAllSelected(false);
            }

            triggerSelectAll(false);
        }
    };

    return (
        <div className={styles.messagingTools}>
            <label
                htmlFor="checkbox"
                className={classNames(
                    "ui-checkbox",
                    styles.checkbox,
                    allSelected && "checked",
                    count === 0 && styles.disabled,
                )}
                onClick={onSelectAllClick}>
                <input type="checkbox" id="checkbox" />
                <i />
                <Translate
                    tid="account_messages_selectAll"
                    namespace={TranslationScopes.UserAccount}
                />
            </label>
            <Button
                type="button"
                className={styles.delete}
                // disabled={!selectedIds?.length}
                disabled
                onClick={onRemoveClick}>
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/message-bin.svg`}
                    className="svg-icon"
                />
            </Button>
            <Button
                type="button"
                className={classNames(
                    styles.starred,
                    selectedIds?.length && starValue && styles.active,
                )}
                disabled={!selectedIds?.length}
                onClick={onStarrClick}>
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/messages-starred.svg`}
                    className="svg-icon"
                />
            </Button>

            <MessagingSearch />
        </div>
    );
};
export default MessagingTools;
